/**
 * Merge homeworks, evaluations and submissions into per-subtask rows.
 * B22: every homework appears even without evaluations.
 * B29: manual SubEvaluation wins over auto; newest auto (highest id) wins otherwise.
 */
export function mergeData(homeworks, evaluations, submissions) {
  const homeworkMap = {};

  // 0) Base: one row per subtask for every homework
  for (const hw of homeworks) {
    homeworkMap[hw.homework_id] = {
      homework_id: hw.homework_id,
      homework_name: hw.homework_name,
      deadline_id: hw.deadline_id,
      deadline_date: hw.deadline_date,
      evaluations: [],
      subtaskRows: {},
    };
    for (const st of hw.subtasks) {
      homeworkMap[hw.homework_id].subtaskRows[st.subtask_id] = {
        subtask_id: st.subtask_id,
        subtask_name: st.subtask_name,
        number: st.number,
        _allSubevals: [],
        eval: null,
        passed: null,
        is_manual: false,
        manually_evaluated_at: null,
        submission: null,
      };
    }
  }

  // 1) Group evaluations; collect subevaluations per subtask
  for (const ev of evaluations) {
    if (!homeworkMap[ev.homework_id]) continue;
    homeworkMap[ev.homework_id].evaluations.push(ev);
    for (const se of ev.subevaluations) {
      const row = homeworkMap[ev.homework_id]?.subtaskRows[se.subtask_id];
      if (row) row._allSubevals.push({ ...se, _eval: ev });
    }
  }

  // 2) B29: pick the winning SubEvaluation per subtask
  for (const hw of Object.values(homeworkMap)) {
    for (const row of Object.values(hw.subtaskRows)) {
      const selist = row._allSubevals;
      if (selist.length === 0) continue;
      const manual = selist.find(se => se.is_manual);
      const valid = manual || selist.reduce((a, b) =>
        a._eval.evaluation_id > b._eval.evaluation_id ? a : b
      );
      row.passed = valid.passed;
      row.is_manual = valid.is_manual;
      row.manually_evaluated_at = valid.manually_evaluated_at ?? null;
      row.eval = valid._eval;
    }
  }

  // 3) Attach newest submission per subtask (backend sends desc order)
  for (const sub of submissions) {
    for (const hw of Object.values(homeworkMap)) {
      const row = hw.subtaskRows[sub.subtask_id];
      if (row && !row.submission) row.submission = sub;
    }
  }

  // 4) Return in backend homework order; rows sorted by subtask number
  return homeworks
    .filter(hw => homeworkMap[hw.homework_id])
    .map(hw => {
      const h = homeworkMap[hw.homework_id];
      return {
        ...h,
        rows: Object.values(h.subtaskRows)
          .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
          .map(({ _allSubevals, ...rest }) => rest), // eslint-disable-line no-unused-vars
      };
    });
}

export default class Student {
    constructor({
        id = null,
        uco = null,
        semester_id = null,
        name = null
    }) {
        this.id = id;
        this.uco = uco;
        this.semester_id = semester_id;
        this.name = name;
    }

    // Basic validation for student data
    static validate(studentData) {
        if (!studentData.uco) {
            throw new Error('UCO is required');
        }
        if (!studentData.semester_id) {
            throw new Error('Semester ID is required');
        }
    }
}
